import { ApiErrorCode, ApiResponse, withApiErrorHandler } from '@/modules/common/api/errors';
import {
  mapCustomAttributeFromResponse,
  mapCustomAttributeTypeToDTO,
} from '@/modules/common/mappers/custom-attribute.mapper';
import { CustomAttribute, CustomAttributeType } from '@/modules/common/types/custom-attribute';
import {
  CreateCustomAttributeRequest,
  UpdateCustomAttributeRequest,
} from '@/modules/common/types/dto/request/custom-attribute-request';
import { CustomAttributeResponse } from '@/modules/common/types/dto/response/custom-attribute-response';
import { EntityTypeDTO } from '@/modules/common/types/dto/response/entity-type-dto';
import { axiosInstance } from '@/utils/axios';

const ENDPOINT = '/api/custom-attributes';

/**
 * Input for creating a member custom attribute definition.
 * `optionValues` are the raw option strings the admin defined; the backend assigns ids.
 */
export interface CreateCustomAttributeInput {
  name: string;
  type: CustomAttributeType;
  optionValues: string[];
}

/**
 * Input for updating an existing custom attribute definition.
 * Existing options keep their numeric id; newly added options omit it.
 */
export interface UpdateCustomAttributeInput {
  name: string;
  selectOptions: { id?: number; value: string }[];
}

/**
 * Creates a custom attribute definition scoped to team members (entityType USER).
 */
export const createCustomAttribute = async (
  input: CreateCustomAttributeInput
): Promise<CustomAttribute> => {
  const body: CreateCustomAttributeRequest = {
    entityType: EntityTypeDTO.USER,
    name: input.name,
    type: mapCustomAttributeTypeToDTO(input.type),
    selectOptions: input.optionValues,
  };

  const response = await withApiErrorHandler(
    ENDPOINT,
    ApiErrorCode.CUSTOM_ATTRIBUTES_SAVE_FAILED,
    () => axiosInstance.post<ApiResponse<CustomAttributeResponse>>(ENDPOINT, body)
  );

  return mapCustomAttributeFromResponse(response);
};

/**
 * Updates a custom attribute definition's name and option list.
 * The backend does not support changing `type`.
 */
export const updateCustomAttribute = async (
  id: number,
  input: UpdateCustomAttributeInput
): Promise<CustomAttribute> => {
  const endpoint = `${ENDPOINT}/${id}`;

  const body: UpdateCustomAttributeRequest = {
    name: input.name,
    selectOptions: input.selectOptions,
  };

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.CUSTOM_ATTRIBUTES_SAVE_FAILED,
    () => axiosInstance.patch<ApiResponse<CustomAttributeResponse>>(endpoint, body)
  );

  return mapCustomAttributeFromResponse(response);
};

/**
 * Deletes a custom attribute definition by id.
 */
export const deleteCustomAttribute = async (id: number): Promise<void> => {
  const endpoint = `${ENDPOINT}/${id}`;

  await withApiErrorHandler(endpoint, ApiErrorCode.CUSTOM_ATTRIBUTES_DELETE_FAILED, () =>
    axiosInstance.delete(endpoint)
  );
};
